"use client"

import { useEffect, useRef, useState } from "react"

import { resendSignupEmail } from "@/features/auth/actions"
import { track } from "@/shared/lib/analytics"
import { createClient } from "@/shared/lib/supabase/client"

const RESEND_COOLDOWN_SECONDS = 3 * 60
const CONFIRM_POLL_INTERVAL_MS = 3000

export type VerificationSurface = "modal" | "page"
export type VerificationContext = "signup" | "login_unverified"

interface UseEmailVerificationOptions {
  /** The address awaiting confirmation. Empty/null while the view is hidden. */
  email: string
  surface: VerificationSurface
  context: VerificationContext
  /** "preview" short-circuits the real resend + confirmation polling. */
  mode?: "live" | "preview"
  /** Called once when a session is detected (email confirmed elsewhere). */
  onConfirmed: () => void
}

/**
 * Shared logic for the post-signup "check your email" / unverified-login view.
 *
 * Single-sources three things that used to be copy-pasted across the auth
 * modal, register page, and login page:
 *  1. resend button state (cooldown timer, loading, error, success)
 *  2. instrumentation — fires `auth:verify_email_view` on appear and
 *     `auth:verify_email_resend` on each resend (with the rate-limiter outcome)
 *  3. live confirmation — polls for the auth-token cookie the callback route
 *     sets, so a user who clicks the emailed link in another tab of the same
 *     browser is advanced automatically instead of staring at a dead end.
 */
export function useEmailVerification({
  email,
  surface,
  context,
  mode = "live",
  onConfirmed,
}: UseEmailVerificationOptions) {
  const [resendCooldown, setResendCooldown] = useState(0)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [resendSuccess, setResendSuccess] = useState(false)

  // Fire the view event once when the screen first appears. Signup just sent a
  // fresh email, so seed the full cooldown; an unverified login did not.
  const appearedRef = useRef(false)
  useEffect(() => {
    if (!email || appearedRef.current) return
    appearedRef.current = true
    track("auth:verify_email_view", { surface, context })
    if (context === "signup") setResendCooldown(RESEND_COOLDOWN_SECONDS)
  }, [email, surface, context])

  // Cooldown countdown.
  useEffect(() => {
    if (resendCooldown <= 0) return
    const id = setInterval(
      () => setResendCooldown((s) => (s <= 1 ? 0 : s - 1)),
      1000
    )
    return () => clearInterval(id)
  }, [resendCooldown])

  // Live confirmation. The confirmation link is opened elsewhere and completes
  // via /api/auth/callback, which sets the (JS-readable) auth-token cookie on
  // this domain. Watch for it appearing, then confirm the resulting session
  // actually belongs to the address awaiting verification before advancing —
  // a different account signing in in another tab must not hijack this view.
  // The identity match below is what guards against a false positive, so we
  // keep monitoring even when a cookie is already present at mount (it may be
  // stale, or belong to another account that later gets replaced by the real
  // confirmation).
  const onConfirmedRef = useRef(onConfirmed)
  onConfirmedRef.current = onConfirmed
  useEffect(() => {
    if (!email || mode !== "live") return
    const pendingEmail = email.trim().toLowerCase()
    const hasAuthToken = () =>
      document.cookie
        .split(";")
        .some((cookie) => cookie.trim().startsWith("auth-token"))

    let done = false
    const supabase = createClient()
    const id = setInterval(async () => {
      if (done || !hasAuthToken()) return
      const {
        data: { session },
      } = await supabase.auth.getSession()
      if (done) return
      if (session?.user?.email?.toLowerCase() !== pendingEmail) return
      done = true
      clearInterval(id)
      onConfirmedRef.current()
    }, CONFIRM_POLL_INTERVAL_MS)
    return () => {
      done = true
      clearInterval(id)
    }
  }, [email, mode])

  async function handleResend() {
    if (!email || resendCooldown > 0 || resendLoading) return

    if (mode === "preview") {
      setResendError(null)
      setResendSuccess(true)
      setResendCooldown(RESEND_COOLDOWN_SECONDS)
      return
    }

    setResendLoading(true)
    setResendError(null)
    setResendSuccess(false)
    const result = await resendSignupEmail({ email })
    setResendLoading(false)

    if (result.error) {
      setResendError(result.error)
      track("auth:verify_email_resend", {
        surface,
        context,
        outcome: result.rateLimited ? "rate_limited" : "error",
      })
      return
    }

    setResendSuccess(true)
    setResendCooldown(RESEND_COOLDOWN_SECONDS)
    track("auth:verify_email_resend", { surface, context, outcome: "success" })
  }

  return { resendCooldown, resendLoading, resendError, resendSuccess, handleResend }
}
